import { useRouter } from 'next/router';

import { useFiltersState } from '../../../../features/filters/Filters.context';
import { useLocationContext } from '../../../../features/location/Location.context';

import {
  formatPathParameters,
  getFilterPathParameterData,
} from '../../../../helpers/seo/searchParams';
import {
  getFilterValue,
  getRangeValue,
} from '../../../../components/SearchPage/helpers/formatting';
import { asString } from '../../../../utils/query-parameters';
import { findCountyByValue } from '../../../../features/location/helpers';
import { mapMakeModelQuery } from '../../../../components/SearchPage/features/makeModel/MakeModel.map';
import { shouldShowTrims } from '../../features/makeModel/MakeModel.constants';

const useSEOFilterValues = () => {
  const { query } = useRouter();
  const { section, make, ...rest } = query;

  const [currentSectionQueryValue] = Array.isArray(section) ? section : [];
  const { filtersData } = useFiltersState();
  const { countyList } = useLocationContext();

  const { makePathParameter, modelPathParameter, SEOFilterValue } =
    formatPathParameters(section);
  const { formattedFilterName, filterValue } = getFilterPathParameterData(
    filtersData,
    SEOFilterValue,
  );

  const {
    year_from,
    year_to,
    fuelType,
    colour,
    transmission,
    bodyType,
    adType,
    area: areaQueryValue,
    sellerType,
    trim,
  } = rest;

  const wanted = Boolean(Array.isArray(adType) ? adType[0] : adType);
  const formattedMakeQuery = asString(make);

  const mappedMakeModelQuery = formattedMakeQuery
    ? mapMakeModelQuery(formattedMakeQuery)
    : undefined;

  /*
   * If the user is coming from the homepage, the make and model value may be formatted as a query.
   * Until the homepage formats these values as path parameters, we look for a query value here.
   */
  const makeQueryValue = mappedMakeModelQuery?.make;
  const modelQueryValue = mappedMakeModelQuery?.model;
  const trimQueryValues = Array.isArray(trim)
    ? trim.map((t) => decodeURIComponent(t))
    : trim
        ?.split(',')
        .filter(Boolean)
        .map((t) => decodeURIComponent(t));

  const includeMakeModelQueryValue = Boolean(
    !makePathParameter &&
      makeQueryValue &&
      (!modelQueryValue || modelQueryValue.length === 1),
  );

  const makeValue = [
    ...(makePathParameter ? [makePathParameter] : []),
    ...(includeMakeModelQueryValue && makeQueryValue ? [makeQueryValue] : []),
  ];

  const modelValue = [
    ...(modelPathParameter ? [modelPathParameter] : []),
    ...(includeMakeModelQueryValue && modelQueryValue?.[0]
      ? [modelQueryValue[0]]
      : []),
  ];

  // If we have one trim we'll use it for SEO, otherwise drop it and just use make/model

  const trimValues =
    shouldShowTrims(currentSectionQueryValue) && trimQueryValues?.length === 1
      ? trimQueryValues
      : undefined;

  const multiMakes =
    (Array.isArray(makeQueryValue) && makeQueryValue.length > 1) ||
    (Array.isArray(make) && make.length > 1);

  const multiModels =
    (Array.isArray(modelQueryValue) && modelQueryValue.length > 1) ||
    (Array.isArray(rest.model) && rest.model.length > 1);

  const multiTrims = trimQueryValues && trimQueryValues.length > 1;

  const years = getRangeValue(
    filtersData,
    'year',
    year_from,
    year_to,
    formattedFilterName,
    filterValue,
  );
  const fuelTypes = getFilterValue(
    filtersData,
    'fuelType',
    fuelType,
    formattedFilterName,
    filterValue,
  );
  const colours = getFilterValue(
    filtersData,
    'colour',
    colour,
    formattedFilterName,
    filterValue,
  );
  const transmissions = getFilterValue(
    filtersData,
    'transmission',
    transmission,
    formattedFilterName,
    filterValue,
  );
  const bodyTypes = getFilterValue(
    filtersData,
    'bodyType',
    bodyType,
    formattedFilterName,
    filterValue,
  );

  const formatQueryAsArray = (queryValue?: string) => {
    return queryValue ? [queryValue] : undefined;
  };

  const countyPathParam = findCountyByValue(countyList, SEOFilterValue);
  const areasByQuery = Array.isArray(areaQueryValue)
    ? areaQueryValue.flatMap(
        (item) => findCountyByValue(countyList, item)?.value ?? [],
      )
    : formatQueryAsArray(areaQueryValue);

  const areas = countyPathParam?.value
    ? [countyPathParam?.value]
    : areasByQuery;

  const isPrivateSellerAd = asString(sellerType) === 'private';

  return {
    wanted,
    years,
    fuelTypes,
    colours,
    transmissions,
    bodyTypes,
    makeValue,
    modelValue,
    trimValues,
    areas,
    isPrivateSellerAd,
    multiMakes,
    multiModels,
    multiTrims,
  };
};

export { useSEOFilterValues };
